import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, TrendingUp, QrCode, LogOut } from 'lucide-react';
import SavingsCalculator from '../components/closer/SavingsCalculator';
import ActivationsTab from '../components/closer/ActivationsTab';
import MyCodeTab from '../components/closer/MyCodeTab';
import { authService } from '../services/auth';
import { closerAPI } from '../utils/api';
import { Toaster } from '../components/ui/toaster';

// Panel de closers (Módulo 9) — mobile-first, para uso en piso.
// Mismo patrón de bottom-nav píldora que Home.jsx.
const CloserPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calculator');
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setUser(currentUser);

    closerAPI
      .getMe()
      .then((res) => {
        if (res?.success) setProfile(res.data);
      })
      .catch(() => {});
  }, [navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-gray-500 text-base">Cargando...</div>
      </div>
    );
  }

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const tabs = [
    { id: 'calculator', icon: Calculator },
    { id: 'activations', icon: TrendingUp },
    { id: 'code', icon: QrCode },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'calculator': return <SavingsCalculator />;
      case 'activations': return <ActivationsTab />;
      case 'code': return <MyCodeTab profile={profile} />;
      default: return null;
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'Closer';

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col">
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        paddingTop: 'max(20px, env(safe-area-inset-top, 20px))',
        paddingLeft: 22, paddingRight: 22, paddingBottom: 14,
        background: 'rgba(245,245,247,0.92)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>Panel de ventas</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
            Hola, {firstName}
          </div>
        </div>
        <button
          onClick={handleLogout}
          title="Cerrar sesión"
          style={{
            width: 40, height: 40, borderRadius: 9999, border: 'none', cursor: 'pointer',
            background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
          }}>
          <LogOut size={17} color="#6B7280" />
        </button>
      </div>

      {/* Contenido con transición entre tabs */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 overflow-y-auto pb-32"
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      {/* Bottom Navigation — píldora */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '0 20px max(28px, env(safe-area-inset-bottom, 28px))',
        display: 'flex', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-evenly',
          width: '100%', maxWidth: 380,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 9999,
          padding: '6px 12px',
          boxShadow: '0 4px 28px rgba(0,0,0,0.13)',
          pointerEvents: 'all',
        }}>
          {tabs.map(({ id, icon: Icon }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                style={{
                  flex: 1, height: 52, borderRadius: 9999,
                  background: isActive ? '#0A0A0A' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background .18s ease',
                }}>
                <Icon size={20} color={isActive ? '#fff' : '#A1A1A6'} />
              </button>
            );
          })}
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default CloserPanel;
